import { useState, useEffect } from 'react';
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useChatbotBaseMutations } from "@/hooks/mutations/useChatbotBaseMutations";
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ImageIcon, Upload, X } from 'lucide-react';
import { useToast } from "@/components/ui/use-toast";
import { Label } from "@/components/ui/label";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

interface OperatorSettingsProps {
  chatbotId: string;
  operatorName?: string | null;
  operatorAvatarUrl?: string | null;
  onSave?: () => void; // Added onSave callback prop
}

export const OperatorSettings = ({ 
  chatbotId,
  operatorName = "", 
  operatorAvatarUrl,
  onSave 
}: OperatorSettingsProps) => {
  const [name, setName] = useState(operatorName || "");
  const [isHovering, setIsHovering] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  const { updateChatbot, uploadOperatorAvatar } = useChatbotBaseMutations(chatbotId);
  const { toast } = useToast();

  useEffect(() => {
    setName(operatorName || "");
  }, [operatorName]);

  const handleSave = () => {
    updateChatbot.mutate({ 
      operator_name: name || null 
    }, {
      onSuccess: () => {
        toast({
          title: "Settings saved",
          description: "Operator settings have been updated successfully.",
        });
        // Call the onSave callback to close the modal
        if (onSave) onSave();
      }
    });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Check file type
    if (!file.type.match(/image\/(jpeg|png|gif|webp)/)) {
      toast({
        variant: "destructive",
        title: "Invalid file type",
        description: "Please upload an image file (JPEG, PNG, GIF, WEBP)",
      });
      return;
    }
    
    // Check file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        variant: "destructive",
        title: "File too large",
        description: "File size should be less than 5MB",
      });
      return;
    }
    
    setIsUploading(true);
    
    uploadOperatorAvatar.mutate({ file }, {
      onSuccess: () => {
        setIsUploading(false);
        toast({
          title: "Avatar uploaded",
          description: "Operator avatar has been updated successfully.",
        });
      },
      onError: (error) => {
        setIsUploading(false);
        toast({
          variant: "destructive",
          title: "Upload failed",
          description: error instanceof Error ? error.message : "An error occurred during upload",
        });
      }
    });
  };
  
  const handleDeleteAvatar = () => {
    setIsUploading(true);
    updateChatbot.mutate({ 
      operator_avatar_url: null 
    }, {
      onSuccess: () => {
        setIsUploading(false);
        setShowDeleteConfirm(false);
        toast({
          title: "Avatar removed",
          description: "Operator avatar has been removed successfully.",
        });
      },
      onError: (error) => {
        setIsUploading(false);
        setShowDeleteConfirm(false);
        toast({
          variant: "destructive",
          title: "Removal failed",
          description: error instanceof Error ? error.message : "An error occurred while removing the avatar",
        });
      }
    });
  };
  
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  return (
    <div className="flex flex-col space-y-4 py-4">
      <div className="flex flex-col space-y-2">
        <Label htmlFor="operator-name">Operator Name</Label>
        <Input
          id="operator-name"
          placeholder="Enter operator name"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <p className="text-sm text-gray-500">
          This name will be displayed as the chat operator in conversations
        </p>
      </div>
      
      <div className="flex flex-col space-y-2">
        <Label>Operator Avatar</Label>
        <div className="flex items-center space-x-4">
          <div 
            className="relative cursor-pointer"
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
          >
            <label htmlFor="operator-avatar-upload" className="cursor-pointer block">
              <Avatar className="h-16 w-16">
                {operatorAvatarUrl ? (
                  <AvatarImage src={operatorAvatarUrl} alt={name} />
                ) : (
                  <AvatarFallback>{getInitials(name || "Operator")}</AvatarFallback>
                )}
              </Avatar>
              
              {isHovering && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full">
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="text-white"
                    disabled={isUploading}
                    onClick={(e) => {
                      e.preventDefault();
                      const input = document.getElementById('operator-avatar-upload') as HTMLInputElement;
                      if (input) input.click();
                    }}
                  >
                    {isUploading ? (
                      <span className="animate-spin">⏳</span>
                    ) : (
                      <Upload size={24} />
                    )}
                  </Button>
                  
                  {operatorAvatarUrl && !isUploading && (
                    <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
                      <AlertDialogTrigger asChild>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          className="text-white absolute bottom-0 right-0"
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                          }}
                        >
                          <X size={16} />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Remove Avatar</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to remove the operator avatar? This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={handleDeleteAvatar}>Remove</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>
              )}
            </label>
            <input
              id="operator-avatar-upload"
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              className="hidden"
              onChange={handleFileChange}
              disabled={isUploading}
            />
          </div>
          
          <div className="flex-1">
            <p className="text-sm text-gray-500">
              Upload an avatar for the chat operator. This will be displayed in the chat header.
            </p>
          </div>
        </div>
      </div>
      
      <Button onClick={handleSave} className="w-full mt-4">
        Save Changes
      </Button>
    </div>
  );
};
